import './videoContent.scss';
import identifyActions from 'actions/identify';
import IdentificationMethodContext from 'components/newAccountSteps/profileSteps/profileStep3/IdentificationMethod/IdentificationMethodContext/IdentificationMethodContext';
import { EnglishMessage } from 'components/newAccountSteps/profileSteps/profileStep3/videocall/messages/englishMessage';
import { NotRecommendedMessage } from 'components/newAccountSteps/profileSteps/profileStep3/videocall/messages/notRecommendedMessage';
import {
    checkVideoPermission,
    isVideoCallAvailable,
    showEnglishMessage,
    isValidBrowser,
} from 'components/newAccountSteps/profileSteps/profileStep3/videocall/videocallHelper';

import VideoClause from './VideoClause/VideoClause';
import { VideoPermissionsHelperModal } from 'components/newIdentifyMethods/videoModals/VideoPermissionsHelperModal';
import { VideoPermissionsModal } from 'components/newIdentifyMethods/videoModals/VideoPermissionsModal';
import { IBAN, TRANSFER_CERTIFICATE, VIDEO_SELFIE } from 'constants/identityMethod';
import { IDENTITY_CONST, IDENTITY_VIDEOS_RECORD_TAG } from 'constants/uTagConstants/paths';
import { EVENT_ONBOARDING } from 'constants/uTagConstants/RAConstants';
import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { actions as formActions } from 'react-redux-form';
import { bindActionCreators } from 'redux';
import { useIdentityView } from 'routes/formsView/IdentityVerificationViews/IdentityView/contexts/IdentityViewContext/IdentityViewContext';
import {
    BROWSER_MAP,
    getBrowserName,
    getOSName,
    isAndroid,
    isBlink,
    isInternetExplorer,
    isMobile,
    isSafari,
    isWindows,
} from 'utilities/browser';
import { getReferenceCodeByCountry, isCountrySpain } from 'utilities/multiLanguageHelper';
import { recordEventCustomOnly, recordIdentityEvent } from 'utilities/uTagging';
import { QRCode } from './components/QRCode';
import { SafeDataAdvise } from './components/SafeDataAdvise';
import VideoSelfieBiometricsChoice from 'components/newIdentifyMethods/video/VideoSelfieBiometricsChoice/VideoSelfieBiometricsChoice';
import withFeatureToggling from '../../../hocs/withFeatureToggling/withFeatureToggling';
import {
    VIDEO_CALL_CHROME_IOS_FLAG,
    VIDEO_SELFIE_CHROME_IOS_FLAG,
    VIDEOSELFIE_BIOMETRY_DECISION,
    VIDEOSELFIE_CAPTURE,
} from 'constants/featureFlagging';
import VideoErrorMessages from './VideoErrorMessages/VideoErrorMessages';
import { getIsXpressIdSupported } from 'utilities/video/xpressIdCompatibility';
import { doTaggingOnCheckboxClick } from './tagging';

const VideoContent = ({
    flags,
    formActions,
    options,
    userProfile: { videoSelected, requestingPermissions, grantedPermission, agreeVideoCallClauseCheck, investmentType },
    storage,
    isIbanActive,
}) => {
    const { onClickOption } = useContext(IdentificationMethodContext);
    const { isSelectedVideoselfieWithBiometrics } = useIdentityView();

    const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);
    const [currentBrowser, setCurrentBrowser] = useState(undefined);
    const [showHelperModal, setShowHelperModal] = useState(false);
    const [modalPicture, setModalPicture] = useState(undefined);
    const [videoClause, setVideoClause] = useState({
        videoSelfie: false,
        videoCall: false,
    });
    const isVideoSelfieSelected = videoSelected === VIDEO_SELFIE;
    const isVeridasVideoSelfieActive = isVideoSelfieSelected && flags.get(VIDEOSELFIE_CAPTURE);
    const isCheckPermissionsActive = !isVeridasVideoSelfieActive || isAndroid();
    const isBiometryDecisionActive = flags?.get(VIDEOSELFIE_BIOMETRY_DECISION);

    const getIdentificationData = () => storage.get('identification') || {};
    const getVideoType = () => (isVideoSelfieSelected ? 'videoSelfie' : 'videoCall');

    useEffect(() => {
        const handleBeforeUnload = () => {
            const identification = getIdentificationData();
            const videoType = getVideoType();
            if (identification.modalShown && !identification[videoType]) {
                const updatedIdentification = {
                    ...identification,
                    [videoType]: {
                        videoCallClause: videoClause[videoType],
                        agreeVideoCallClauseCheck: videoClause[videoType],
                    },
                };

                storage.set('identification', updatedIdentification);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoClause]);

    useEffect(() => {
        const checkPermissionsRequest = async () => {
            if (isBlink()) {
                const cameraPermission = await navigator.permissions?.query({ name: 'camera' });
                const microphonePermission = await navigator.permissions?.query({ name: 'microphone' });

                return (
                    requestingPermissions &&
                    (cameraPermission?.state === 'prompt' || microphonePermission?.state === 'prompt')
                );
            }

            return requestingPermissions;
        };

        checkPermissionsRequest().then(res => setIsRequestingPermissions(!!res));
    }, [requestingPermissions]);

    useEffect(() => {
        const identification = getIdentificationData();
        if (identification?.modalShown && !requestingPermissions && !grantedPermission) {
            checkVideoPermission({
                grantedPermission,
                changeFunction: formActions.change,
                videoSelected,
            });
        }

        setShowHelperModal(grantedPermission === false);

        if (identification?.modalShown && agreeVideoCallClauseCheck && grantedPermission) {
            window.scrollTo(0, document.body.scrollHeight);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grantedPermission, requestingPermissions]);

    useEffect(() => {
        const BROWSER_LIST = [BROWSER_MAP.chrome, BROWSER_MAP.firefox, BROWSER_MAP.safari, BROWSER_MAP.edge];
        const browserName = BROWSER_LIST.includes(getBrowserName()) && !isMobile() ? getBrowserName() : 'Other';

        setCurrentBrowser(browserName);

        const loadPicture = async () => {
            const { default: importedPicture } =
                browserName === 'Other'
                    ? await import(`assets/images/video-permissions-helper/Other.svg`)
                    : await import(
                          `assets/images/video-permissions-helper/${browserName}/${getReferenceCodeByCountry()}.gif`
                      );

            return importedPicture;
        };

        currentBrowser &&
            loadPicture().then(picture => {
                setModalPicture(picture);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getBrowserName(), currentBrowser]);

    const handleVideoClause = value => {
        const videoType = getVideoType();
        setVideoClause(prevState => ({ ...prevState, [videoType]: value }));
        formActions.change('userProfile.videoCallClause', value);

        doTaggingOnCheckboxClick(value);

        recordIdentityEvent(
            EVENT_ONBOARDING,
            IDENTITY_CONST,
            `${value ? 'aceptar condiciones' : 'error aceptar clausulas'} ${IDENTITY_VIDEOS_RECORD_TAG[videoSelected]}`
        );
    };

    const videoPermissionsHelperModalActions = {
        cancel: () => {
            const identification = getIdentificationData();
            const videoType = getVideoType();
            delete identification[videoType];
            storage.set('identification', { ...identification, modalShown: false });
            onClickOption(isCountrySpain() ? IBAN : TRANSFER_CERTIFICATE);
            recordEventCustomOnly({
                categoriaEvento: 'onboarding/identidad/',
                accionEvento: `modal/videoselfie-permisos-instrucciones/${currentBrowser}`,
                etiquetaEvento: 'cambio-a-transferencia',
            });
        },
        confirm: () => {
            window.location.reload();
            recordEventCustomOnly({
                categoriaEvento: 'onboarding/identidad/',
                accionEvento: `modal/videoselfie-permisos-instrucciones/${currentBrowser}`,
                etiquetaEvento: 'ya-concedido',
            });
        },
    };

    const videoChromeFlag =
        videoSelected === isVideoSelfieSelected
            ? flags.get(VIDEO_SELFIE_CHROME_IOS_FLAG)
            : flags.get(VIDEO_CALL_CHROME_IOS_FLAG);

    const englishMessage = !isVideoSelfieSelected && showEnglishMessage() ? <EnglishMessage /> : null;
    const explorerMessage = isInternetExplorer() && isWindows() && <NotRecommendedMessage />;
    const videoCallAvailable = isVideoCallAvailable({ options, videoSelected });
    const shouldShowQR = isVideoSelfieSelected && !isMobile() && !isVeridasVideoSelfieActive;
    const shouldShowVideoPermisionModal = !isMobile() && !isSafari() && isCheckPermissionsActive;

    const isVideoIdentificationSupported = isVeridasVideoSelfieActive
        ? getIsXpressIdSupported()
        : isValidBrowser(videoChromeFlag);
    const showVideoClause = videoCallAvailable && isVideoIdentificationSupported;

    return (
        <div className="clause-container">
            {explorerMessage}
            {englishMessage}
            <SafeDataAdvise />
            {shouldShowQR && <QRCode />}
            {showVideoClause && (
                <VideoClause
                    handleVideoClause={handleVideoClause}
                    isBiometricDecisionActive={isBiometryDecisionActive}
                    isSelectedVideoselfieWithBiometrics={isSelectedVideoselfieWithBiometrics}
                    storage={storage}
                />
            )}
            <VideoSelfieBiometricsChoice
                isVideoSelfieSelected={isVideoSelfieSelected}
                isBiometricDecisionActive={isBiometryDecisionActive}
            />
            <VideoErrorMessages isInvalidBrowser={!isVideoIdentificationSupported} investmentType={investmentType} />
            {shouldShowVideoPermisionModal && <VideoPermissionsModal showModal={isRequestingPermissions} />}
            {isCheckPermissionsActive && modalPicture && (
                <VideoPermissionsHelperModal
                    isIbanActive={isIbanActive}
                    showModal={showHelperModal}
                    onCancel={videoPermissionsHelperModalActions.cancel}
                    onConfirm={videoPermissionsHelperModalActions.confirm}
                    browser={getBrowserName()}
                    picture={modalPicture}
                    pictureName={currentBrowser}
                    isMobile={isMobile()}
                    videoType={videoSelected}
                    osName={getOSName()}
                    storage={storage}
                />
            )}
        </div>
    );
};

export default connect(
    state => ({
        userProfile: state.userProfile,
        options: state.options,
    }),
    dispatch => ({
        identifyActions: bindActionCreators(identifyActions, dispatch),
        formActions: bindActionCreators(formActions, dispatch),
    })
)(withFeatureToggling(VideoContent));
